import {
  expireStaffSession,
  getStaffSessionToken,
  isStaffSessionExpiredError,
  onStaffSignOutFailed,
} from "@/services/staff-session.services";
import { getStaffUser, resolveStaffSession } from "@/services/staff.services";
import { CipUser } from "@EspaceParkQC/sdk-services-legacy";
import { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";

type StaffSessionMode = "checking" | "staff" | "none";

interface StaffSessionValue {
  mode: StaffSessionMode;
  user?: CipUser;
  error?: unknown;
}

const StaffSessionContext = createContext<StaffSessionValue>({ mode: "checking" });

/** Decides once how this page is signed in, then loads the staff user. Requests read the token themselves. */
export function StaffSessionProvider({ children }: PropsWithChildren): React.ReactElement {
  const [value, setValue] = useState<StaffSessionValue>({ mode: "checking" });

  useEffect(() => {
    let cancelled = false;

    // Shown as the critical error, whose Logout button clears what the sign-out could not.
    const stopListening = onStaffSignOutFailed((error) => {
      if (!cancelled) setValue((current) => ({ ...current, error }));
    });

    void resolveStaffSession().then(async (isStaff) => {
      if (cancelled) return;
      if (!isStaff) {
        setValue({ mode: "none" });
        return;
      }

      setValue({ mode: "staff" });

      try {
        const token = await getStaffSessionToken();
        if (!token) throw new Error("No staff member is signed in.");

        const user = await getStaffUser(token);
        if (!cancelled) setValue((current) => ({ ...current, user }));
      } catch (error) {
        // An answer that comes after this provider is gone must not end whichever session runs now.
        if (cancelled) return;

        // The API's clock decides: a sign-in it finds too old ends here, whatever this browser's clock says.
        if (isStaffSessionExpiredError(error)) void expireStaffSession();
        else setValue((current) => ({ ...current, error }));
      }
    });

    return () => {
      cancelled = true;
      stopListening();
    };
  }, []);

  return <StaffSessionContext.Provider value={value}>{children}</StaffSessionContext.Provider>;
}

export function useStaffSession(): StaffSessionValue {
  return useContext(StaffSessionContext);
}

/** The signed-in agent and their clicknpark API credential: a function that reads the staff token before each request. */
export function useCallCenterSession(): {
  user?: CipUser;
  getToken: () => Promise<string | undefined>;
} {
  const staff = useStaffSession();

  return { user: staff.user, getToken: getStaffSessionToken };
}
